'use client'

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import Link from 'next/link'
import { AppDispatch, RootState } from '@/store'
import { fetchSuggestData } from '@/store/suggest/slice'
import { SuggestCompany, SuggestTag } from '@/lib/api/services/suggest'
import clsx from 'clsx'
import styles from './styles.module.scss'

interface Suggestion {
  companies: SuggestCompany[]
  categories: SuggestTag[]
}

interface SearchInputSuggestionsProps {
  used?: string
}

export default function SearchInputSuggestions({ used = '' }: SearchInputSuggestionsProps) {
  const dispatch = useDispatch<AppDispatch>()
  const suggestData = useSelector((state: RootState) => state.suggest.suggestData)
  const [searchWord, setSearchWord] = useState('')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<Suggestion>({
    companies: [],
    categories: [],
  })
  const inputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0, width: 0 })

  // コンポーネントマウント時にサジェストデータを取得
  useEffect(() => {
    dispatch(fetchSuggestData())
  }, [dispatch])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // サジェストフィルター関数
  const filterSuggestions = useCallback(
    (searchWord: string) => {
      if (!suggestData) return { companies: [], categories: [] }

      const searchWords = searchWord.toLowerCase().split(' ').filter(Boolean)

      // 企業名でフィルター
      const nameFilteredCompanies = suggestData.companies
        .filter((company: any) =>
          searchWords.every((word) => company.name.toLowerCase().includes(word)),
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 3)

      // 企業名でと3社未満の場合、openUrl、enName、kanaで検索
      let additionalCompanies: any[] = []
      if (nameFilteredCompanies.length < 3) {
        additionalCompanies = suggestData.companies
          .filter((company: any) => {
            return searchWords.every(
              (word) =>
                !nameFilteredCompanies.includes(company) && // すでに見つかった企業を除外
                (company.openUrl.toLowerCase().includes(word) ||
                  company.enName?.toLowerCase().includes(word) ||
                  company.kana?.toLowerCase().includes(word)),
            )
          })
          .sort((a, b) => a.name.localeCompare(b.name))
          .slice(0, 3 - nameFilteredCompanies.length) // 残りのスロットを埋める
      }

      const companies = [...nameFilteredCompanies, ...additionalCompanies]

      // カテゴリーをフィルター
      const categories = suggestData.tags
        .filter((tag: any) => searchWords.every((word) => tag.name.toLowerCase().includes(word)))
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 3)

      return { companies, categories }
    },
    [suggestData],
  )

  // ポータルの位置を計算
  const updatePortalPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      setPortalPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      })
    }
  }, [])

  // スクロールとリサイズ時に位置を更新
  useEffect(() => {
    if (!isMenuOpen) return

    const handleScroll = () => {
      updatePortalPosition()
    }

    const handleResize = () => {
      updatePortalPosition()
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [isMenuOpen, updatePortalPosition])

  useEffect(() => {
    if (searchWord.trim() === '') {
      setSuggestions({ companies: [], categories: [] })
      setIsMenuOpen(false)
      return
    }

    const filtered = filterSuggestions(searchWord)
    setSuggestions(filtered)
    const shouldOpen = filtered.companies.length > 0 || filtered.categories.length > 0
    setIsMenuOpen(shouldOpen)

    if (shouldOpen) {
      updatePortalPosition()
    }
  }, [searchWord, suggestData, filterSuggestions, updatePortalPosition])

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchWord(e.target.value)
  }

  const closeMenu = () => {
    setIsMenuOpen(false)
  }

  const submitSearch = () => {
    if (searchWord) {
      const encodedQuery = encodeURIComponent(searchWord.trim())
      resetSearchWord()
      window.location.href = `/search?query=${encodedQuery}`
    }
  }

  const resetSearchWord = () => {
    setSearchWord('')
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      submitSearch()
    }
  }

  return (
    <div className={clsx(styles.searchContents, used && styles[used])}>
      <div className={styles.searchItem}>
        <i className='icon-font-icon_search' />
        <input
          ref={inputRef}
          value={searchWord}
          placeholder={used === 'error' ? 'キーワードを入力' : '企業名・職種・キーワードを入力'}
          className={styles.searchInput}
          type='text'
          onChange={handleInput}
          onKeyPress={handleKeyPress}
          onClick={() => {
            if (searchWord.trim() !== '') {
              updatePortalPosition()
              setIsMenuOpen(true)
            }
          }}
        />
      </div>
      <button className={styles.searchSubmitButton} onClick={submitSearch}>
        {used === 'headerSuggestion' ? <i className='icon-font-icon_search' /> : <span>検索</span>}
      </button>

      {/* サジェストウィンドウ */}
      {typeof window !== 'undefined' &&
        isMenuOpen &&
        searchWord &&
        createPortal(
          <div
            ref={menuRef}
            className={clsx(styles.suggestWindow, styles.open)}
            style={{
              position: 'fixed',
              top: `${portalPosition.top}px`,
              left: `${portalPosition.left}px`,
              width: `${portalPosition.width}px`,
              zIndex: 9999,
            }}
          >
            <div className={clsx(styles.searchContentsGroup, styles.searchWord)}>
              <Link
                href={`/search?query=${searchWord}`}
                className={styles.suggestLink}
                onClick={resetSearchWord}
              >
                <i className='icon-font-icon_search' />
                <div className={styles.searchText}>
                  <span className={styles.searchWord}>{searchWord}</span>
                  で検索
                </div>
              </Link>
            </div>

            {/* company */}
            {suggestions.companies.length > 0 && (
              <div className={styles.searchContentsGroup}>
                <div className={clsx(styles.suggestItem, styles.suggestTitle)}>企業</div>
                <ul className={styles.suggestList}>
                  {suggestions.companies.slice(0, 3).map((company, index) => (
                    <li
                      key={`company-${index}-${company.id}`}
                      className={styles.suggestItem}
                      onClick={closeMenu}
                    >
                      <Link
                        href={`/${company.openUrl}`}
                        className={styles.suggestLink}
                        onClick={resetSearchWord}
                      >
                        <img
                          src={company.imageUrl || '/images/svg/dummy_logo.svg'}
                          alt={company.name}
                          className={styles.suggestLogImg}
                          loading='lazy'
                        />
                        <span className={styles.suggestName}>{company.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* category */}
            {suggestions.categories.length > 0 && (
              <div className={styles.searchContentsGroup}>
                <div className={clsx(styles.suggestItem, styles.suggestTitle)}>カテゴリ</div>
                <ul className={styles.suggestList}>
                  {suggestions.categories.slice(0, 3).map((category, index) => (
                    <li
                      key={`category-${index}-${category.id}`}
                      className={styles.suggestItem}
                      onClick={closeMenu}
                    >
                      <Link
                        href={`/categories/${category.slug}`}
                        className={styles.suggestLink}
                        onClick={resetSearchWord}
                      >
                        <i className='icon-font-icon_tag' />
                        <span className={styles.suggestName}>{category.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}
